import { useState, useEffect, useRef } from "react";
import styles from "./complete-admin-profile.module.css";
import { useTranslation } from "react-i18next";
import RegisterPresentation from "components/register/register-presentation";
import useIsMobile from "hooks/detect-mobile-screen";
import instance from "services/api";
import { useNavigate } from "react-router";
import Spinner from "components/functional/spinner";
import { ToastService, ToastComponent } from "services/toast.service";

const CompleteAdminProfile = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile(980);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const organizationNameInputRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!firstName) newErrors.firstName = t("errors.required");
    if (!lastName) newErrors.lastName = t("errors.required");
    if (!organizationName) newErrors.organizationName = t("errors.required");
    if (!address) newErrors.address = t("errors.required");
    if (!postalCode) newErrors.postalCode = t("errors.required");
    if (!city) newErrors.city = t("errors.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendInformations = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!validateForm()) return;
      const response = await instance.put("admins/complete-profile", {
        first_name: firstName,
        last_name: lastName,
        organization: {
          name: organizationName,
          address,
          city,
          postal_code: postalCode,
        },
      });

      let { team } = response.data;
      let { team_id } = team;

      navigate("/first-step", { state: { role: "admin", team_id } });
    } catch (error) {
      ToastService.showErrorToast(
        t("onboarding.admin.informations-step.error")
      );
      console.error("Error updating profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationNameInputRef.current) {
      organizationNameInputRef.current.focus();
    }
  }, []);

  return (
    <div className={styles.container}>
      <ToastComponent />
      <div className={styles.wrapper}>
        <form className={styles.content} onSubmit={sendInformations}>
          <header className={styles.header}>
            <img src="/logo.png" width="26" alt="logo" />
            <h1 className="title-1-variant-low">Kompa</h1>
          </header>
          <div className={styles.form}>
            <div className={styles["company-informations"]}>
              <div className="form-group mb-48">
                <label htmlFor="organizationName" className="required">
                  {t(
                    "onboarding.admin.informations-step.company-data.organization-name"
                  )}
                </label>
                <input
                  id="organizationName"
                  type="text"
                  placeholder={t(
                    "onboarding.admin.informations-step.company-data.organization-name-placeholder"
                  )}
                  className={`${styles.input} ${
                    errors.organizationName && "error"
                  }`}
                  value={organizationName}
                  onChange={(e) => {
                    setOrganizationName(e.target.value);
                    setErrors({ ...errors, organizationName: null });
                  }}
                  ref={organizationNameInputRef}
                />
                {errors.organizationName && (
                  <p className="error-message">{t("errors.required")}</p>
                )}
              </div>
              <div className={styles["company-fields"]}>
                <div className="form-group">
                  <label htmlFor="address" className="required">
                    {t(
                      "onboarding.admin.informations-step.company-data.address"
                    )}
                  </label>
                  <input
                    type="text"
                    id="address"
                    className={`grey-input ${errors.address && "error"}`}
                    value={address}
                    onChange={(e) => {
                      setAddress(e.target.value);
                      setErrors({ ...errors, address: null });
                    }}
                    placeholder={t(
                      "onboarding.admin.informations-step.company-data.address-placeholder"
                    )}
                  />
                  {errors.address && (
                    <p className="error-message">{t("errors.required")}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="postalCode" className="required">
                    {t(
                      "onboarding.admin.informations-step.company-data.postal-code"
                    )}
                  </label>
                  <input
                    type="tel"
                    id="postalCode"
                    className={`grey-input ${errors.postalCode && "error"}`}
                    value={postalCode}
                    onChange={(e) => {
                      setPostalCode(e.target.value);
                      setErrors({ ...errors, postalCode: null });
                    }}
                    placeholder={t(
                      "onboarding.admin.informations-step.company-data.postal-code-placeholder"
                    )}
                  />
                  {errors.postalCode && (
                    <p className="error-message">{t("errors.required")}</p>
                  )}
                </div>
              </div>
              <div className={styles.city}>
                <div className="form-group">
                  <label htmlFor="city" className="required">
                    {t("onboarding.admin.informations-step.company-data.city")}
                  </label>
                  <input
                    type="text"
                    id="city"
                    className={`grey-input ${errors.city && "error"}`}
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      setErrors({ ...errors, city: null });
                    }}
                    placeholder={t(
                      "onboarding.admin.informations-step.company-data.city-placeholder"
                    )}
                  />
                  {errors.city && (
                    <p className="error-message">{t("errors.required")}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={styles["personal-informations"]}>
              <div className={styles["personal-header"]}>
                <h3 className="title-3">
                  {t(
                    "onboarding.admin.informations-step.personal-informations.title"
                  )}
                </h3>
                <p className="subtitle-1-variant">
                  {t(
                    "onboarding.admin.informations-step.personal-informations.description"
                  )}
                </p>
              </div>
              <div className={styles["personal-fields"]}>
                <div className="form-group">
                  <label htmlFor="firstName" className="required">
                    {t(
                      "onboarding.admin.informations-step.personal-data.first-name"
                    )}
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    className={`grey-input ${errors.firstName && "error"}`}
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setErrors({ ...errors, firstName: null });
                    }}
                    placeholder={t(
                      "onboarding.admin.informations-step.personal-data.first-name-placeholder"
                    )}
                  />
                  {errors.firstName && (
                    <p className="error-message">{t("errors.required")}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="lastName" className="required">
                    {t(
                      "onboarding.admin.informations-step.personal-data.last-name"
                    )}
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className={`grey-input ${errors.lastName && "error"}`}
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setErrors({ ...errors, lastName: null });
                    }}
                    placeholder={t(
                      "onboarding.admin.informations-step.personal-data.last-name-placeholder"
                    )}
                  />
                  {errors.lastName && (
                    <p className="error-message">{t("errors.required")}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.actions}>
            <button type="submit" className="btn btn--primary">
              {t("onboarding.admin.informations-step.cta")}
              {loading && <Spinner className="action-loader" />}
            </button>
          </div>
        </form>
        {!isMobile && <RegisterPresentation />}
      </div>
    </div>
  );
};

export default CompleteAdminProfile;
